import { useEffect, type ReactNode } from "react";
import { Badge, cn } from "../../../../components/ui";
import { useMxLabels } from "../../hooks/useMxLabels";
import { costOf, dueOf, fmtDate, fmtMoney, photosOf } from "../../lib";
import { CATEGORY_ICONS, STATUS_ACTIONS, STATUS_TRANSITIONS, categoryOf } from "../../theme";
import type { MaintenanceRecord, MaintenanceStatus, VehicleOption } from "../../types";
import { Photo, PhotoGallery } from "./Media";
import { DueText, ModeBadge, StatusBadge, TypeBadge } from "./Primitives";

export interface MaintenanceDrawerProps {
  record: MaintenanceRecord;
  vehicle?: VehicleOption | null;
  onClose: () => void;
  canManage?: boolean;
  canCharge?: boolean;
  onTransition?: (r: MaintenanceRecord, s: MaintenanceStatus) => void;
  onEdit?: (r: MaintenanceRecord) => void;
  onDelete?: (r: MaintenanceRecord) => void;
  onPdf?: (r: MaintenanceRecord) => void;
  onCharge?: (r: MaintenanceRecord) => void;
  onOpenVehicle?: (vehicleId: string) => void;
}

// Panel lateral de detalle (drawer): foto de la unidad, estado, acciones,
// datos de la orden, galería de evidencia, desglose de costo y línea de tiempo.
export function MaintenanceDrawer({ record: r, vehicle, onClose, canManage, canCharge, onTransition, onEdit, onDelete, onPdf, onCharge, onOpenVehicle }: MaintenanceDrawerProps) {
  const L = useMxLabels();
  const { t } = L;
  const status = r.status || "pendiente";
  const nexts = canManage ? STATUS_TRANSITIONS[status] ?? [] : [];
  const photos = photosOf(r);
  const cat = categoryOf(r.maintenanceDescription);
  const money = (n?: number) => fmtMoney(n, L.locale);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const timeline = [
    { key: "opened", at: r.openedAt, icon: "ri-file-add-line" },
    { key: "scheduled", at: r.scheduledDate, icon: "ri-calendar-check-line" },
    { key: "started", at: r.startedAt, icon: "ri-play-circle-line" },
    { key: "closed", at: r.closedAt || r.completedDate, icon: status === "cancelado" ? "ri-close-circle-line" : "ri-checkbox-circle-line" },
  ].filter((s) => s.at);
  if (status !== "completado" && status !== "cancelado" && dueOf(r) && !r.scheduledDate) timeline.push({ key: "due", at: dueOf(r), icon: "ri-alarm-line" });
  timeline.sort((a, b) => Date.parse(a.at || "") - Date.parse(b.at || ""));

  return (
    <div className="fixed inset-0 z-[1100] flex justify-end" role="dialog" aria-modal="true" aria-label={t("mantenimiento.ui.drawer.title")}>
      <button type="button" aria-label={t("mantenimiento.ui.drawer.close")} className="absolute inset-0 bg-black/40 animate-fadeIn" onClick={onClose} />
      <aside className="relative flex h-full w-full max-w-[520px] flex-col overflow-hidden border-l border-line bg-surface shadow-2xl animate-slideIn">
        {/* Hero con la foto de la unidad */}
        <div className="relative h-52 shrink-0">
          <Photo src={vehicle?.image} alt={vehicle?.label || ""} fallbackIcon="ri-truck-line" className="h-full w-full" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          <button type="button" onClick={onClose} aria-label={t("mantenimiento.ui.drawer.close")} className="absolute right-3 top-3 rounded-full bg-black/40 p-2 text-white hover:bg-black/60">
            <i className="ri-close-line text-lg" />
          </button>
          <div className="absolute bottom-3 left-4 right-4 text-white">
            <p className="text-[11px] uppercase tracking-wider text-white/70">{t("mantenimiento.ui.drawer.vehicle")}</p>
            <p className="truncate text-lg font-semibold">{vehicle?.label ?? r.vehicleId}</p>
            <p className="text-xs text-white/80">
              {[vehicle?.plate, vehicle?.brand && `${vehicle.brand} ${vehicle.model ?? ""}`.trim(), vehicle?.year].filter(Boolean).join(" · ")}
            </p>
          </div>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-1.5">
              <StatusBadge record={r} />
              <TypeBadge type={r.type} size="md" />
              <ModeBadge mode={r.workshopMode} size="md" />
              <Badge tone="neutral" size="md">
                <i className={CATEGORY_ICONS[cat]} />
                {L.category(cat)}
              </Badge>
            </div>
            <h2 className="text-base font-semibold text-content">{r.maintenanceDescription || "—"}</h2>
            <DueText record={r} />
          </div>

          {/* Acciones */}
          <div className="flex flex-wrap gap-2">
            {nexts.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => onTransition?.(r, s as MaintenanceStatus)}
                className={cn("inline-flex items-center gap-1.5 rounded-lg border border-line px-3 py-1.5 text-xs font-medium", STATUS_ACTIONS[s]?.className)}
              >
                <i className={STATUS_ACTIONS[s]?.icon} />
                {L.transition(s)}
              </button>
            ))}
            {onPdf ? (
              <button type="button" onClick={() => onPdf(r)} className="inline-flex items-center gap-1.5 rounded-lg border border-line px-3 py-1.5 text-xs font-medium text-content-secondary hover:bg-subtle">
                <i className="ri-file-pdf-line" />
                {t("mantenimiento.workOrderPdf")}
              </button>
            ) : null}
            {canCharge && r.workshopMode === "externo" && r.paymentStatus !== "paid" && onCharge ? (
              <button type="button" onClick={() => onCharge(r)} className="inline-flex items-center gap-1.5 rounded-lg border border-line px-3 py-1.5 text-xs font-medium text-success hover:bg-success/10">
                <i className="ri-money-dollar-circle-line" />
                {t("mantenimiento.charge")}
              </button>
            ) : null}
            {canManage && onEdit ? (
              <button type="button" onClick={() => onEdit(r)} className="inline-flex items-center gap-1.5 rounded-lg border border-line px-3 py-1.5 text-xs font-medium text-primary-500 hover:bg-primary-500/10">
                <i className="ri-edit-line" />
                {t("mantenimiento.ui.drawer.edit")}
              </button>
            ) : null}
            {canManage && onDelete ? (
              <button type="button" onClick={() => onDelete(r)} className="inline-flex items-center gap-1.5 rounded-lg border border-line px-3 py-1.5 text-xs font-medium text-error hover:bg-error/10">
                <i className="ri-delete-bin-line" />
                {t("mantenimiento.delete")}
              </button>
            ) : null}
          </div>

          {/* Datos */}
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-xl border border-line bg-card p-4 text-xs">
            <Info label={t("mantenimiento.fields.workshop")} value={r.workshopName} wide />
            <Info label={t("mantenimiento.fields.mechanic")} value={r.assignedMechanic} />
            <Info label={t("mantenimiento.fields.odometer")} value={r.odometer ? t("mantenimiento.ui.drawer.km", { value: Number(r.odometer).toLocaleString(L.locale) }) : null} />
            <Info label={t("mantenimiento.ui.drawer.nextService")} value={[r.nextMaintenanceDate && fmtDate(r.nextMaintenanceDate, L.locale), r.nextMaintenanceOdometer ? t("mantenimiento.ui.drawer.km", { value: Number(r.nextMaintenanceOdometer).toLocaleString(L.locale) }) : null].filter(Boolean).join(" · ") || null} />
            <Info label={t("mantenimiento.ui.drawer.requestedBy")} value={r.requestedBy} />
            {r.diagnosis ? <Info label={t("mantenimiento.fields.diagnosis")} value={r.diagnosis} wide /> : null}
            {r.notes ? <Info label={t("mantenimiento.fields.notes")} value={r.notes} wide /> : null}
          </dl>

          {/* Evidencia */}
          <Block title={t("mantenimiento.ui.drawer.evidence")} icon="ri-image-2-line" count={photos.length}>
            {photos.length ? <PhotoGallery photos={photos} alt={t("mantenimiento.ui.drawer.evidence")} /> : <p className="text-xs text-content-muted">{t("mantenimiento.ui.drawer.noPhotos")}</p>}
          </Block>

          {/* Costo */}
          <Block title={t("mantenimiento.ui.drawer.costBreakdown")} icon="ri-money-dollar-box-line">
            {(r.parts?.length || r.serviceItems?.length || r.laborCost) ? (
              <div className="space-y-1.5 text-xs">
                {r.laborCost ? <Line label={t("mantenimiento.ui.drawer.labor")} value={money(r.laborCost)} /> : null}
                {(r.parts ?? []).map((p, i) => (
                  <Line key={`p${i}`} label={`${p.name} × ${p.quantity ?? 1}`} value={money(p.total ?? Number(p.unitCost || 0) * Number(p.quantity || 1))} muted />
                ))}
                {(r.serviceItems ?? []).map((s, i) => (
                  <Line key={`s${i}`} label={`${s.name} × ${s.quantity ?? 1}`} value={money(Number(s.price || 0) * Number(s.quantity || 1))} muted />
                ))}
              </div>
            ) : (
              <p className="text-xs text-content-muted">{t("mantenimiento.ui.drawer.noLines")}</p>
            )}
            <div className="mt-3 flex items-center justify-between border-t border-line pt-3">
              <span className="text-sm font-semibold text-content">{t("mantenimiento.ui.drawer.total")}</span>
              <span className="text-base font-bold tabular-nums text-content">{money(costOf(r))}</span>
            </div>
            <div className="mt-1 flex items-center justify-between text-xs">
              <span className="text-content-secondary">{t("mantenimiento.ui.drawer.charged")}</span>
              {r.workshopMode === "externo" ? (
                <Badge tone={r.paymentStatus === "paid" ? "success" : r.paymentStatus === "pending" ? "warning" : "neutral"} size="sm">
                  {r.paymentStatus === "paid" ? t("mantenimiento.paid") : r.paymentStatus === "pending" ? t("mantenimiento.pending") : t("mantenimiento.unpaid")}
                </Badge>
              ) : (
                <span className="text-content-muted">{t("mantenimiento.free")}</span>
              )}
            </div>
          </Block>

          {/* Línea de tiempo */}
          {timeline.length ? (
            <Block title={t("mantenimiento.ui.drawer.timeline")} icon="ri-git-commit-line">
              <ol className="relative ml-2 space-y-3 border-l border-line pl-4">
                {timeline.map((s) => (
                  <li key={s.key} className="relative">
                    <span className="absolute -left-[25px] flex h-4 w-4 items-center justify-center rounded-full bg-card ring-2 ring-primary-500">
                      <i className={cn(s.icon, "text-[9px] text-primary-500")} />
                    </span>
                    <p className="text-xs font-medium text-content">{t(`mantenimiento.ui.drawer.${s.key}`)}</p>
                    <p className="text-[11px] text-content-muted">{fmtDate(s.at, L.locale, true)}</p>
                  </li>
                ))}
              </ol>
            </Block>
          ) : null}

          {r.attachments?.length ? (
            <Block title={t("mantenimiento.ui.drawer.attachments")} icon="ri-attachment-2">
              <ul className="space-y-1 text-xs">
                {r.attachments.map((a, i) => (
                  <li key={i}>
                    {a.url ? (
                      <a href={a.url} target="_blank" rel="noreferrer" className="text-primary-500 hover:underline">
                        <i className="ri-file-line mr-1" />
                        {a.name}
                      </a>
                    ) : (
                      <span className="text-content-secondary">
                        <i className="ri-file-line mr-1" />
                        {a.name}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </Block>
          ) : null}

          {onOpenVehicle ? (
            <button type="button" onClick={() => onOpenVehicle(r.vehicleId)} className="flex w-full items-center justify-center gap-2 rounded-lg border border-line py-2 text-sm text-content-secondary hover:bg-subtle hover:text-content">
              <i className="ri-history-line" />
              {t("mantenimiento.ui.drawer.openVehicle")}
            </button>
          ) : null}
        </div>
      </aside>
    </div>
  );
}

function Info({ label, value, wide }: { label: string; value?: ReactNode; wide?: boolean }) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <dt className="text-[11px] uppercase tracking-wide text-content-muted">{label}</dt>
      <dd className="mt-0.5 text-content">{value || "—"}</dd>
    </div>
  );
}

function Block({ title, icon, count, children }: { title: string; icon: string; count?: number; children: ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-content-muted">
        <i className={icon} />
        {title}
        {count ? <span className="rounded-full bg-subtle px-1.5 text-[10px] text-content-secondary">{count}</span> : null}
      </h3>
      {children}
    </section>
  );
}

function Line({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className={muted ? "text-content-secondary" : "text-content"}>{label}</span>
      <span className="shrink-0 tabular-nums text-content">{value}</span>
    </div>
  );
}
